import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../../../../components/toast';
import { Alert, Button, ButtonMenu, Typography, Icon } from '@ann/web-components';
import {
  DriveWorkspaceMainWrapper,
  DriveWorkspaceContainer,
  DetailHeader,
  DetailHeaderLeft,
  FiltersRow,
  BackToTopButton,
  BackButton,
} from './DriveWorkspace.styled';
import Filters from '../MyWorkspace/components/Filters/Filters';
import { useStore } from '../../../../../hooks';
import { useDrivesStores } from '../../hook/use-drives.hook';
import { useStoreNotifications } from '../../hook/use-store-notifications.hook';
import { useFileUpload } from '../../hook/use-file-upload.hook';
import { useFolderHierarchy } from '../../hooks/useFolderHierarchy';
import DriveBreadcrumb from './components/DriveBreadcrumb';
import ActiveFolder from '../ActiveFolder';
import { BannerSelected } from '../../components/BannerSelected';
import RenameModal from '../../components/RenameModal';

// New extracted hooks
import { useScrollManagement } from './hooks/useScrollManagement';
import { useFilterAndSort } from './hooks/useFilterAndSort';
import { useDriveWorkspaceData } from './hooks/useDriveWorkspaceData';
import { useDriveWorkspaceActions } from './hooks/useDriveWorkspaceActions';

// New extracted components
import { UploadFeedbackSection } from './components/UploadFeedbackSection';
import { DriveWorkspaceContent } from './components/DriveWorkspaceContent';
import { DriveWorkspaceModals } from './components/DriveWorkspaceModals';

const DriveWorkspace = () => {
  const { vaultUuid: vaultUuidFromParams } = useParams();
  const navigate = useNavigate();
  const { foldersStore, documentsStore, agentsStore, resourcesStore, usersStore, authStore } = useStore();
  const { drivesViewStore } = useDrivesStores();
  const toast = useToast();
  const userId = authStore.userId;

  // Use useFolderHierarchy to detect drive context and nested folders
  const { vaultUuid, folderHierarchy, isDriveContext } = useFolderHierarchy();
  
  // Use vaultUuid from hook if available, otherwise from params (backward compatibility)
  const finalVaultUuid = vaultUuid || vaultUuidFromParams;
  
  // Check if we're viewing a nested folder (not drive root)
  const isViewingFolder = isDriveContext && folderHierarchy.length > 0;

  // Extract primitive value to avoid array dependency
  const drivesLength = drivesViewStore?.drivesStore?.drives?.length ?? 0;

  // Get drive name from drivesStore
  const driveName = useMemo(() => {
    if (!finalVaultUuid || drivesLength === 0) {
      return 'Loading...';
    }
    const drive = drivesViewStore.drivesStore.drives.find(d => d.id === finalVaultUuid);
    return drive?.title || 'Drive';
  }, [finalVaultUuid, drivesLength, drivesViewStore]);

  // Stores are singletons - capture them once in a ref
  const storesRef = useRef({ foldersStore, documentsStore, agentsStore, resourcesStore, usersStore });

  // ===== HOOKS =====

  // Data loading hook
  const {
    driveWorkspaceStore,
    storeRef,
    folders,
    documents,
    isDataLoading,
    setFolders,
    setDocuments,
    folderListLength,
    documentListLength,
  } = useDriveWorkspaceData({
    finalVaultUuid,
    isViewingFolder,
    drivesViewStore,
    storesRef,
  });

  // Scroll management hook
  const { scrollContainerRef, showBackToTop, scrollToTop } = useScrollManagement();

  // File upload hook
  const handleUploadComplete = useCallback(async () => {
    const currentStore = storeRef.current;
    await currentStore.loadWorkspaceData();
    setFolders(currentStore.folderList || []);
    setDocuments(currentStore.documentList || []);
  }, [storeRef, setFolders, setDocuments]);

  const { fileInputRef, fileInputProps, handleDrop, openFileSelector, retryUpload } = useFileUpload({
    onUploadComplete: handleUploadComplete,
    vaultUuid: finalVaultUuid,
    folderId: !isViewingFolder ? driveWorkspaceStore.rootFolderUuid : undefined,
  });

  // Actions hook
  const actions = useDriveWorkspaceActions({
    storeRef,
    toast,
    userId,
    navigate,
    openFileSelector,
  });

  // Filter and sort hook
  const filterSort = useFilterAndSort({
    folders,
    documents,
    storeRef,
    foldersHasMore: driveWorkspaceStore.foldersHasMore,
    isLoadingMoreFolders: driveWorkspaceStore.isLoadingMoreFolders,
    documentsHasMore: driveWorkspaceStore.documentsHasMore,
    isLoadingMoreDocuments: driveWorkspaceStore.isLoadingMoreDocuments,
  });

  // Expose file selector function to store
  useEffect(() => {
    const currentStore = storeRef.current;
    currentStore.setFileInputRef(fileInputRef);
  }, [fileInputRef, storeRef]);

  // Update selectedDocument when it changes in the store
  const selectedDocumentUuid = actions.selectedDocument?.document_uuid || 
                                actions.selectedDocument?.documentUuid || 
                                actions.selectedDocument?.id;
  useEffect(() => {
    if (selectedDocumentUuid) {
      const currentStore = storeRef.current;
      const updatedDoc = currentStore.documentList?.find(
        (doc) =>
          doc.document_uuid === selectedDocumentUuid ||
          doc.documentUuid === selectedDocumentUuid ||
          doc.id === selectedDocumentUuid
      );
      if (updatedDoc && updatedDoc.name !== actions.selectedDocument?.name) {
        actions.setSelectedDocument(updatedDoc);
      }
    }
  }, [documentListLength, selectedDocumentUuid, actions.selectedDocument?.name, storeRef, actions]);

  // ===== NOTIFICATIONS =====
  
  // Extract primitive values for notifications
  const foldersStoreCreatedFolder = driveWorkspaceStore?.foldersStore?.createdFolder;
  const foldersStoreFolderCreatedName = driveWorkspaceStore?.foldersStore?.folderCreated?.name;
  const foldersStoreFolderAlreadyExists = driveWorkspaceStore?.foldersStore?.folderAlreadyExists;
  const foldersStoreIsErrorCreateFolder = driveWorkspaceStore?.foldersStore?.isErrorCreateFolder;
  const documentsStoreUploadSuccess = documentsStore?.uploadSuccess;
  const documentsStoreUploadFailed = documentsStore?.uploadFailed;
  const documentsStoreErrorMessage = documentsStore?.errorMessage;

  useStoreNotifications([
    {
      condition: foldersStoreCreatedFolder,
      type: 'success',
      description: `${foldersStoreFolderCreatedName} was created`,
      onClose: () => {
        storeRef.current?.foldersStore?.setCreatedFolder(false);
      }
    },
    {
      condition: foldersStoreFolderAlreadyExists,
      type: 'error',
      title: 'A folder with this name already exists.',
      description: 'Please choose a different name.',
      onClose: () => {
        storeRef.current?.foldersStore?.setFolderAlreadyExists(false);
      }
    },
    {
      condition: foldersStoreIsErrorCreateFolder,
      type: 'error',
      title: 'Connection error, folder could not be created.',
      description: 'Please try again or check your connection.',
      onClose: () => {
        storeRef.current?.foldersStore?.setIsErrorCreateFolder(false);
      }
    },
    {
      condition: documentsStoreUploadSuccess,
      type: 'success',
      title: 'Upload successful',
      description: 'Your documents have been uploaded successfully',
      onClose: () => documentsStore?.setUploadSuccess(false)
    },
    {
      condition: documentsStoreUploadFailed,
      type: 'error',
      title: 'Upload failed',
      description: documentsStoreErrorMessage || 'Some files failed to upload',
      onClose: () => {
        documentsStore?.setUploadFailed(false);
        documentsStore?.clearErrorMessage();
      }
    }
  ]);

  // ===== DERIVED VALUES =====
  
  const countSelectedDocuments = driveWorkspaceStore.selectionStore.countSelectedDocuments;
  const countSelectedFolders = driveWorkspaceStore.selectionStore.countSelectedFolders;
  const storeViewMode = driveWorkspaceStore.viewMode;
  const storeIsLoading = driveWorkspaceStore.isLoading;
  const isLoading = storeIsLoading || isDataLoading;
  const isEmpty = folderListLength === 0 && documentListLength === 0;

  // Store modal states
  const isShareModalOpen = driveWorkspaceStore.isShareModalOpen;
  const isDuplicateModalOpen = driveWorkspaceStore.isDuplicateModalOpen;
  const isAssignAgentModalOpen = driveWorkspaceStore.isAssignAgentModalOpen;
  const isMoveModalOpen = driveWorkspaceStore.isMoveModalOpen;
  const isDeleteModalOpen = driveWorkspaceStore.isDeleteModalOpen;
  const documentToDeleteName = driveWorkspaceStore.documentToDelete?.name || '';
  const isDeletingDocument = driveWorkspaceStore.isDeletingDocument;
  const usersList = driveWorkspaceStore.usersStore.users;

  // Handle close upload feedback
  const handleCloseUploadFeedback = useCallback(() => {
    documentsStore.temporaryDocuments.forEach(doc => {
      documentsStore.removeTemporaryDocument(doc.uploadId || doc.name);
    });
  }, [documentsStore]);

  // ===== RENDER =====

  return (
    <>
      {actions.alert && (
        <Alert
          title={actions.alert.message}
          severity={actions.alert.severity}
          onClose={actions.handleCloseAlert}
          maxWidth="400px"
          closable={true}
          autoClose={true}
          autoCloseDelay={3000}
        />
      )}

      {/* Rename Modal for Drive */}
      <RenameModal store={drivesViewStore.renameModalStore} />

      {/* Hidden input for file selection */}
      <input {...fileInputProps} ref={fileInputRef} />

      <DriveWorkspaceMainWrapper ref={scrollContainerRef}>
        <DriveWorkspaceContainer>
          <DriveBreadcrumb />
          
          {/* If viewing a nested folder, use ActiveFolder component */}
          {isViewingFolder ? (
            <ActiveFolder />
          ) : (
            <>
              {/* Header with Drive Name and New ButtonMenu */}
              {countSelectedDocuments === 0 && countSelectedFolders === 0 && (
                <DetailHeader>
                  <DetailHeaderLeft>
                    <BackButton onClick={actions.handleBackToDrives}>
                      <Icon name="arrowLeft" size="medium" />
                    </BackButton>
                    <Typography category="title" weight="bold" size="large">
                      {driveName}
                    </Typography>
                    {/* Small menu next to title with Upload, New Folder, Rename */}
                    <ButtonMenu
                      label=""
                      menuPosition="bottom"
                      onChange={(value) => {
                        if (value === 'upload-documents') {
                          actions.handleUploadDocuments();
                        }
                        if (value === 'new-folder') {
                          actions.handleOpenNewFolderModal();
                        }
                        if (value === 'rename') {
                          drivesViewStore.openRenameModal(finalVaultUuid, driveName, (driveId, newTitle) => {
                            // Reload drives data to reflect the new name
                            drivesViewStore.loadDrivesData();
                          });
                        }
                      }}
                      size="small"
                      type="secondary"
                      width="auto"
                    >
                      <ButtonMenu.Menu icon={true}>
                        <ButtonMenu.Item
                          label="Upload Documents"
                          value="upload-documents"
                          icon="publish"
                        />
                        <ButtonMenu.Item
                          label="New Folder"
                          value="new-folder"
                          icon="createNewFolder"
                        />
                        <ButtonMenu.Item
                          label="Rename"
                          value="rename"
                          icon="edit"
                        />
                      </ButtonMenu.Menu>
                    </ButtonMenu>
                  </DetailHeaderLeft>
                  <ButtonMenu
                    label="New"
                    menuPosition="bottom"
                    onChange={(value) => {
                      if (value === 'new-folder') {
                        actions.handleOpenNewFolderModal();
                      }
                      if (value === 'upload-documents') {
                        actions.handleUploadDocuments();
                      }
                    }}
                    size="medium"
                    type="primary"
                    width="auto"
                    customIcon="plus"
                  >
                    <ButtonMenu.Menu icon={true} minWidth="260px">
                      <ButtonMenu.Item
                        label="Upload Documents"
                        value="upload-documents"
                        icon="publish"
                      />
                      <ButtonMenu.Item
                        label="New Folder"
                        value="new-folder"
                        icon="createNewFolder"
                      />
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                </DetailHeader>
              )}

              {/* Filters */}
              {countSelectedDocuments === 0 && countSelectedFolders === 0 && (
                <FiltersRow>
                  <Filters
                    onFilterChange={filterSort.setFilterText}
                    onSortChange={filterSort.handleSortChange}
                    sortBy={filterSort.sortBy}
                    sortDirection={filterSort.sortDirection}
                    filterText={filterSort.filterText}
                  />
                </FiltersRow>
              )}

              {/* Banner for selected items */}
              <BannerSelected
                store={driveWorkspaceStore}
                onShare={actions.handleBannerShare}
                onMove={actions.handleBannerMove}
              />

              {/* Main content area */}
              <DriveWorkspaceContent
                isLoading={isLoading}
                isEmpty={isEmpty}
                  vaultUuid={finalVaultUuid}
                  openFileSelector={openFileSelector}
                  handleDrop={handleDrop}
                viewMode={storeViewMode}
                visibleFolders={filterSort.visibleFolders}
                visibleDocuments={filterSort.visibleDocuments}
              selectionStore={driveWorkspaceStore.selectionStore}
                openDuplicateModal={driveWorkspaceStore.openDuplicateModal}
                openMoveModal={driveWorkspaceStore.openMoveModal}
                hasMoreFolders={filterSort.hasMoreFolders}
                isLoadingFolders={filterSort.isLoadingFolders}
                loadMoreFolders={filterSort.loadMoreFolders}
                hasMoreDocuments={filterSort.hasMoreDocuments}
                isLoadingDocuments={filterSort.isLoadingDocuments}
                loadMoreDocuments={filterSort.loadMoreDocuments}
                onShare={actions.handleShareModalOpen}
                onAssignAgent={actions.handleAssignAgentModalOpen}
                onRenameFolder={actions.handleOpenRenameFolderModal}
                onRenameDocument={actions.handleOpenRenameDocumentModal}
                onFolderInfo={actions.handleOpenFolderInfo}
                onDelete={actions.handleDeleteDocument}
                onOpenDocumentDetail={actions.handleOpenDocumentDetail}
              />

              {/* All modals and slideouts */}
              <DriveWorkspaceModals
                driveWorkspaceStore={driveWorkspaceStore}
                resourcesStore={driveWorkspaceStore.resourcesStore}
                vaultUuid={finalVaultUuid}
                // Store-based modal states
                isShareModalOpen={isShareModalOpen}
                isDuplicateModalOpen={isDuplicateModalOpen}
                isAssignAgentModalOpen={isAssignAgentModalOpen}
                isMoveModalOpen={isMoveModalOpen}
                isDeleteModalOpen={isDeleteModalOpen}
                documentToDeleteName={documentToDeleteName}
                isDeletingDocument={isDeletingDocument}
          usersList={usersList}
                // Local modal states
                isRenameFolderModalOpen={actions.isRenameFolderModalOpen}
                folderToRename={actions.folderToRename}
                isRenameDocumentModalOpen={actions.isRenameDocumentModalOpen}
                documentToRename={actions.documentToRename}
                showNewFolderModal={actions.showNewFolderModal}
                // Slideout states
                isDocumentDetailOpen={actions.isDocumentDetailOpen}
                selectedDocument={actions.selectedDocument}
                isFolderInfoOpen={actions.isFolderInfoOpen}
                selectedFolder={actions.selectedFolder}
                // Share modal handlers
                onCloseShareModal={actions.handleCloseShareModal}
                onShareAll={actions.handleShareAll}
                onAddToPending={actions.handleAddToPending}
                onUpdatePendingPermission={actions.handleUpdatePendingPermission}
                onRemovePending={actions.handleRemovePending}
                onUpdatePermission={actions.handleUpdatePermission}
                onRemoveAccess={actions.handleRemoveAccess}
                // Duplicate modal handlers
                onCloseDuplicateModal={actions.handleCloseDuplicateModal}
                onDuplicateItem={actions.handleDuplicateItem}
                // Assign agent modal handlers
                onCloseAssignAgentModal={actions.handleCloseAssignAgentModal}
                onAssignAgent={actions.handleAssignAgent}
                // Move modal handlers
                onCloseMoveModal={actions.handleCloseMoveModal}
                // Delete modal handlers
                onCloseDeleteModal={actions.handleCloseDeleteModal}
                onConfirmDelete={actions.handleConfirmDelete}
                // Rename folder modal handlers
                onCloseRenameFolderModal={actions.handleCloseRenameFolderModal}
                onSaveRenameFolder={actions.handleSaveRenameFolder}
                // Rename document modal handlers
                onCloseRenameDocumentModal={actions.handleCloseRenameDocumentModal}
                onSaveRenameDocument={actions.handleSaveRenameDocument}
                onOpenRenameDocumentModal={actions.handleOpenRenameDocumentModal}
                // New folder modal handlers
                onCloseNewFolderModal={actions.handleCloseNewFolderModal}
                onCreateFolder={actions.handleCreateFolder}
                // Document detail slideout handlers
                onCloseDocumentDetail={actions.handleCloseDocumentDetail}
                closeSlideoutThen={actions.closeSlideoutThen}
                onShareModalOpen={actions.handleShareModalOpen}
                onAssignAgentModalOpen={actions.handleAssignAgentModalOpen}
                // Folder info slideout handlers
                onCloseFolderInfo={actions.handleCloseFolderInfo}
        />
            </>
          )}
        </DriveWorkspaceContainer>
      </DriveWorkspaceMainWrapper>

      {/* Upload feedback section */}
      <UploadFeedbackSection
        documentsStore={documentsStore}
        onRetry={retryUpload}
        onClose={handleCloseUploadFeedback}
      />

      {/* Back to top button */}
      <BackToTopButton $visible={showBackToTop}>
        <Button
          type="primary"
          size="medium"
          label="Back to top"
          icon="chevronUp"
          iconPosition="left"
          onClick={scrollToTop}
          pill={true}
        />
      </BackToTopButton>
    </>
  );
};

export default observer(DriveWorkspace);
